import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Download, ArrowLeft, Star, Calendar, Loader2, Tv, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePlayer } from "@/context/PlayerContext";
import Navbar from "@/components/Navbar";
import FavoriteButton from "@/components/FavoriteButton";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getSeriesInfo, imageUrl, proxyStreamUrl } from "@/lib/xtream";
import { toast } from "sonner";

export default function SeriesDetail() {
  const { id } = useParams();
  const { creds } = useAuth();
  const { openPlayer } = usePlayer();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [season, setSeason] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getSeriesInfo(creds, id)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        const keys = Object.keys(d?.episodes || {});
        if (keys.length) setSeason(keys[0]);
      })
      .catch((e) => !cancelled && setError(e?.response?.data?.detail || "Could not load this series."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id, creds]);

  const seasonKeys = useMemo(() => Object.keys(data?.episodes || {}), [data]);
  const episodes = (season && data?.episodes?.[season]) || [];

  if (loading) {
    return (
      <Shell>
        <div className="flex items-center justify-center py-40 text-slate-500">
          <Loader2 className="h-8 w-8 animate-spin text-[#14A3FF]" />
        </div>
      </Shell>
    );
  }
  if (error || !data?.info) {
    return (
      <Shell>
        <div className="text-center py-40 text-slate-400">{error || "Series not found."}</div>
      </Shell>
    );
  }

  const info = data.info || {};
  const backdrop = (Array.isArray(info.backdrop_path) && info.backdrop_path[0]) || info.cover;
  const poster = info.cover;

  const playEpisode = (ep) => proxyStreamUrl(creds, "series", ep.id, ep.container_extension || "mp4");
  const download = (ep) => {
    toast.success("Opening download…");
    window.open(proxyStreamUrl(creds, "series", ep.id, ep.container_extension || "mp4", true), "_blank");
  };
  const play = (ep) => {
    const epInfo = ep.info || {};
    const poster = epInfo.movie_image || info.cover;
    openPlayer({
      url: playEpisode(ep),
      title: `${info.name} — S${season}·E${ep.episode_num}${ep.title ? ` · ${ep.title}` : ""}`,
      poster: poster ? imageUrl(poster) : null,
      meta: {
        kind: "episode",
        playableId: String(ep.id),
        seriesId: String(id),
        ext: ep.container_extension || "mp4",
        name: `${info.name} — S${season}E${ep.episode_num}`,
        poster: info.cover || poster || null,
      },
    });
  };

  return (
    <Shell>
      <div className="relative h-[46vh] min-h-[320px] w-full">
        {backdrop ? (
          <img src={imageUrl(backdrop)} alt="" className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-[#0C1017]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#05070A] via-[#05070A]/70 to-[#05070A]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#05070A]/90 to-transparent" />
        <div className="relative mx-auto max-w-[1600px] px-4 sm:px-8 lg:px-12 pt-6">
          <Button
            data-testid="back-button"
            onClick={() => navigate(-1)}
            variant="ghost"
            className="text-slate-200 hover:text-white hover:bg-white/10"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
          </Button>
        </div>
      </div>

      <div className="relative mx-auto max-w-[1600px] px-4 sm:px-8 lg:px-12 -mt-36">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-3">
            <div className="aspect-[2/3] rounded-2xl overflow-hidden border border-white/10 bg-[#131924] shadow-2xl">
              {poster ? (
                <img src={imageUrl(poster)} alt={info.name} className="h-full w-full object-cover" />
              ) : (
                <div className="h-full w-full flex items-center justify-center text-slate-700">
                  <Tv className="h-12 w-12" />
                </div>
              )}
            </div>
          </motion.div>

          <div className="lg:col-span-9 pt-2">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white font-heading mb-3">
              {info.name}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-300 mb-5">
              {info.rating ? (
                <span className="flex items-center gap-1.5 text-[#E5A93C] font-semibold">
                  <Star className="h-4 w-4 fill-[#E5A93C]" /> {parseFloat(info.rating).toFixed(1)}
                </span>
              ) : null}
              {info.releaseDate ? (
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-4 w-4" /> {String(info.releaseDate).slice(0, 4)}
                </span>
              ) : null}
              {info.genre ? (
                <span className="px-2.5 py-0.5 rounded-full bg-white/10 text-xs">{info.genre}</span>
              ) : null}
              <span className="text-slate-500">
                {seasonKeys.length} season{seasonKeys.length !== 1 ? "s" : ""}
              </span>
            </div>
            {info.plot ? <p className="text-slate-300 leading-relaxed max-w-3xl">{info.plot}</p> : null}
            <div className="mt-5">
              <FavoriteButton
                creds={creds}
                kind="series"
                item={{ series_id: id, name: info.name, cover: info.cover, rating: info.rating, releaseDate: info.releaseDate }}
                variant="button"
              />
            </div>
          </div>
        </div>

        {/* Episodes */}
        <div className="mt-10">
          <div className="flex items-center justify-between gap-4 mb-5">
            <h2 className="text-2xl font-bold text-white font-heading">Episodes</h2>
            {seasonKeys.length > 0 && (
              <Select value={season} onValueChange={setSeason}>
                <SelectTrigger
                  data-testid="series-season-select"
                  className="w-44 bg-[#131924] border-white/10 text-white"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0C1017] border-white/10 text-white">
                  {seasonKeys.map((k) => (
                    <SelectItem key={k} value={k} className="focus:bg-white/10 focus:text-white">
                      Season {k}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-3 max-w-4xl">
            {episodes.map((ep) => {
              const epInfo = ep.info || {};
              const thumb = epInfo.movie_image;
              return (
                <motion.div
                  key={ep.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-4 rounded-xl border border-white/5 bg-[#131924] p-3 hover:border-[#14A3FF]/40 transition-colors"
                  data-testid="episode-row"
                >
                  <div className="relative h-16 w-28 shrink-0 rounded-lg overflow-hidden bg-black/40">
                    {thumb ? (
                      <img src={imageUrl(thumb)} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center text-slate-600">
                        <Tv className="h-5 w-5" />
                      </div>
                    )}
                    <span className="absolute bottom-1 left-1 text-[10px] font-bold bg-black/70 text-white rounded px-1">
                      E{ep.episode_num}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-white line-clamp-1">
                      {ep.title || `Episode ${ep.episode_num}`}
                    </p>
                    {epInfo.duration ? (
                      <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                        <Clock className="h-3 w-3" /> {epInfo.duration}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      data-testid="episode-play-button"
                      onClick={() => play(ep)}
                      size="sm"
                      className="bg-[#14A3FF] hover:bg-[#0E8FE6] text-white active:scale-95 transition-transform"
                    >
                      <Play className="h-4 w-4 sm:mr-1.5 fill-white" />
                      <span className="hidden sm:inline">Play</span>
                    </Button>
                    <Button
                      data-testid="episode-download-button"
                      onClick={() => download(ep)}
                      size="sm"
                      variant="outline"
                      className="border-white/15 bg-white/5 text-white hover:bg-white/10 active:scale-95 transition-transform"
                    >
                      <Download className="h-4 w-4 sm:mr-1.5" />
                      <span className="hidden sm:inline">Download</span>
                    </Button>
                  </div>
                </motion.div>
              );
            })}
            {episodes.length === 0 && (
              <p className="text-slate-500 py-8 text-center">No episodes in this season.</p>
            )}
          </div>
        </div>
      </div>
      <div className="h-16" />
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#05070A]">
    <Navbar />
    {children}
  </div>
);
